<template>
	<div class="flex flex-col gap-0.5 bg-white shadow sm:rounded-lg p-4 m-4">
		<div v-if="!displayForm">
			<div class="flex items-center justify-between w-full gap-2 mb-4">
				<PageLabel :helpPageName="'UserManagement'">User Management</PageLabel>
				<div class="flex flex-row items-center gap-2">
					<Button id="btnNewUser" @click="newUser()" v-if="!displayForm && SessionInfo.hasRightsToScreen('Users', PageAccess.Create)">
						New User
					</Button>
				</div>
			</div>
			<DataTable
				:value="usersList"
				responsiveLayout="scroll"
				:row-hover="true"
				@row-click="rowClick($event)"
				:paginator="true"
				:rows="tablePaginator == null ? 10 : tablePaginator.rows"
				:first="tablePaginator == null ? 0 : tablePaginator.first"
				:page="tablePaginator == null ? 0 : tablePaginator.page"
				@page="pageChanged($event)"
				paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				:rowsPerPageOptions="[10, 20, 50]"
				currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
				dataKey="id"
				v-model:filters="usersFilters"
				filterDisplay="menu"
				@filter="applyFilter($event)"
				:loading="loading1"
				:globalFilterFields="['emailid', 'firstName', 'userType.userTypeName']"
				id="dtUserList"
			>
				<template #header>
					<div class="p-d-flex p-jc-between">
						<Button icon="pi pi-filter-slash" label="Clear" @click="usersFilters['global'].value = null" />
						<span class="relative">
							<i class="pi pi-search absolute inset-y-3 right-3 flex items-center pointer-events-none"></i>
							<InputText class="pl-10" v-model="usersFilters['global'].value" placeholder="Keyword Search" id="UserKeywordSearch" />
						</span>
					</div>
				</template>

				<template #empty> No customers found. </template>
				<template #loading> Loading user data. Please wait. </template>
				<Column field="emailid" header="Email" :sortable="true" style="flex: 0 0 30%">
					<template #body="{ data }">
						{{ data.emailid }}
					</template>
					<template #filter="{ filterModel }">
						<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by email" />
					</template>
				</Column>
				<Column field="firstName" filterField="firstName" header="Name" :sortable="true" style="flex: 0 0 25%">
					<template #body="{ data }">
						<div class="w-full">{{ data.firstName + " " + data.lastName }}</div>
					</template>
					<template #filter="{ filterModel }">
						<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by name" />
					</template>
				</Column>
				<Column field="userType.userTypeName" filterField="userType.userTypeName" header="User Type" :sortable="true" style="flex: 0 0 25%">
					<template #body="{ data }">
						<div class="w-full">{{ data.userType.userTypeName }}</div>
					</template>
					<template #filter="{ filterModel }">
						<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by user type" />
					</template>
				</Column>
				<Column field="lastLogin" filterField="lastLogin" header="Days since last login" :sortable="true" style="flex: 0 0 10%">
					<template #body="{ data }">
						<div class="w-full">{{ data.lastLogin != null ? calculatesDays(data.lastLogin) : "" }}</div>
					</template>
				</Column>
				<Column field="projectCount" header="# of Projects" :sortable="true" style="flex: 0 0 10%">
					<template #body="{ data }">
						<!--	{{ getUserProjectCount(data.id) }}-->
					</template>
				</Column>
			</DataTable>
		</div>
		<div v-if="displayForm">
			<!--<userEdit :personId="selectedUserId" @cancel="cancel()" @refreshList="refresh()" />-->
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive } from "vue"
import { ApiClient, Person } from "../../entities/NSwagGenerated"
import { FilterMatchMode, FilterOperator } from "@primevue/core/api"
import Button from "primevue/button"
import InputText from "primevue/inputtext"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import PageLabel from "./pageLabel.vue"
import DataTable, { DataTableRowEditCancelEvent } from "primevue/datatable"
import Column from "primevue/column"
import userEdit from "./userEdit.vue"

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const usersList = ref()
const userType = ref()
const persons = new Array<Person>()
let displayForm = ref(false)
let tablePaginator: any | null = null
const selectedUserId = ref()
let loading1: any = null

let usersFilters = reactive({
	global: { value: null, matchMode: FilterMatchMode.CONTAINS },
	emailid: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	firstName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	"userType.userTypeName": { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
})

function newUser() {
	displayForm.value = true
}

function cancel() {
	selectedUserId.value = null
	displayForm.value = false
}
function pageChanged(ev: Event) {
	tablePaginator = ev
	localStorage.setItem("userTypePaginate", JSON.stringify(ev))
}
function applyFilter(ev: Event) {
	localStorage.setItem("tableFilter", JSON.stringify(usersFilters))
}
function refresh() {
	apiClient!.selectedColumnUserList().then(
		(result) => {
			usersList.value = result
		},
		(err) => {
			throw err
		}
	)
}

onMounted(() => {
	refresh()
})

function rowClick(e: DataTableRowEditCancelEvent) {
	selectedUserId.value = e.data.id
	displayForm.value = true
}

function calculatesDays(lastLoginDate: Date) {
	const _MS_PER_DAY = 1000 * 60 * 60 * 24
	let dateNow = new Date(Date.now())
	let LoginDate = new Date(lastLoginDate)

	let days = dateDifference(dateNow, LoginDate)
	return days
}
function dateDifference(date2: Date, date1: Date) {
	const _MS_PER_DAY = 1000 * 60 * 60 * 24

	// Discard the time and time-zone information.
	const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate())
	const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate())

	return Math.floor((date2.getTime() - date1.getTime()) / _MS_PER_DAY)
}
function getUserProjectCount(userId: string) {
	//return this.persons.filter((p) => p.id == userId && p.projectPersonperson.project.projectStatus.projectStausName == "Closed").length
	let tempPersons = persons
	let person = tempPersons.filter((p) => p.id == userId)
	let closeCount = 0

	if (person[0].projectPersonperson && person[0].projectPersonperson.length > 0) {
		person[0].projectPersonperson.forEach((item) => {
			if (item.project && item.project != null) {
				if (item.project.projectStatus && item.project.projectStatus != null) {
					if (item.project.projectStatus.projectStatusName == "Closed") {
						closeCount = closeCount + 1
					}
				}
			}
		})
	}

	//return 0
	return closeCount
}
</script>

<style lang="scss" scoped>
::v-deep(.p-paginator) {
	.p-paginator-current {
		margin-left: auto;
	}
}

::v-deep(.p-progressbar) {
	height: 0.5rem;
	background-color: #d8dadc;

	.p-progressbar-value {
		background-color: #607d8b;
	}
}

::v-deep(.p-datepicker) {
	min-width: 25rem;

	td {
		font-weight: 400;
	}
}

::v-deep(.p-datatable.p-datatable-customers) {
	.p-datatable-header {
		padding: 1rem;
		text-align: left;
		font-size: 1.5rem;
	}

	.p-paginator {
		padding: 1rem;
	}

	.p-datatable-thead > tr > th {
		text-align: left;
	}

	.p-datatable-tbody > tr > td {
		cursor: auto;
	}

	.p-dropdown-label:not(.p-placeholder) {
		text-transform: uppercase;
	}
}
</style>
